import React, { useCallback, useState, useEffect, useRef } from "react";
import { DropdownMenu } from "@/components/DropdownMenu";
import { TooltipContainer } from "@scripts/react/components/Tooltip";
import { useMobile } from "@/hooks/useMobile";

interface VideoControlsMenuProps {
    videoRef?: React.RefObject<HTMLVideoElement | null>;
    isPlaying: boolean;
    disabled: boolean;
    onTimelineClick?: (targetTime: number) => void;
    setCurrentTime: (time: number) => void;
    fps: number;
}

type ControlOption = {
    id: string;
    label: string;
    action: () => void;
    icon: string;
    hotkey?: string;
};

export const VideoControlsMenu: React.FC<VideoControlsMenuProps> = ({
    videoRef,
    isPlaying,
    disabled,
    onTimelineClick,
    setCurrentTime,
    fps
}) => {
    const isMobile = useMobile();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const menuDropdownRef = useRef<HTMLDivElement>(null);

    const togglePlayPause = useCallback(async () => {
        if (disabled) {
            console.log("🚫 [PLAY/PAUSE] Blocked during model generation");
            return;
        }

        const video = videoRef?.current;
        if (!video) {
            console.log("🎮 [PLAY/PAUSE] No video element available");
            return;
        }

        if (isPlaying) {
            video.pause();
        } else {
            try {
                await video.play();
            } catch (e) {
                console.error("🎮 [PLAY/PAUSE] Error attempting to play video:", e);
            }
        }
    }, [disabled, videoRef, isPlaying]);

    const skipTime = useCallback(
        (seconds: number) => {
            if (disabled) {
                console.log("🚫 [SKIP TIME] Blocked during model generation");
                return;
            }

            const video = videoRef?.current;
            if (!video) {
                console.log("🎮 [SKIP TIME] No video element available");
                return;
            }

            const newTime = Math.max(0, Math.min(video.currentTime + seconds, video.duration));
            video.currentTime = newTime;
            setCurrentTime(newTime);

            // Only trigger frame update when video is paused to avoid lag during playback
            if (!isPlaying) {
                onTimelineClick?.(newTime);
            }
        },
        [disabled, videoRef, setCurrentTime, onTimelineClick, isPlaying]
    );

    const skipFrame = useCallback(
        (direction: 1 | -1) => {
            if (disabled) {
                console.log("🚫 [SKIP FRAME] Blocked during model generation");
                return;
            }

            const video = videoRef?.current;
            if (!video) {
                console.log("🎮 [SKIP FRAME] No video element available");
                return;
            }

            const frameDuration = 1 / fps;
            const newTime = Math.max(
                0,
                Math.min(video.currentTime + direction * frameDuration, video.duration)
            );
            video.currentTime = newTime;
            setCurrentTime(newTime);

            // Only trigger frame update when video is paused to avoid lag during playback
            if (!isPlaying) {
                onTimelineClick?.(newTime);
            }
        },
        [disabled, videoRef, setCurrentTime, onTimelineClick, isPlaying, fps]
    );

    const menuOptions: ControlOption[] = [
        {
            id: "play-pause",
            label: isPlaying ? "Pause" : "Play",
            action: togglePlayPause,
            icon: isPlaying ? "fas fa-pause" : "fas fa-play",
            hotkey: "Space"
        },
        {
            id: "backward",
            label: "Rewind 1s",
            action: () => skipTime(-1),
            icon: "fas fa-backward-fast",
            hotkey: "←"
        },
        {
            id: "forward",
            label: "Forward 1s",
            action: () => skipTime(1),
            icon: "fas fa-forward-fast",
            hotkey: "→"
        }
        // {
        //     id: "prev-frame",
        //     label: "Previous Frame",
        //     action: () => skipFrame(-1),
        //     icon: "fas fa-step-backward",
        //     hotkey: ","
        // },
        // {
        //     id: "next-frame",
        //     label: "Next Frame",
        //     action: () => skipFrame(1),
        //     icon: "fas fa-step-forward",
        //     hotkey: "."
        // }
    ];

    // Monitor dropdown open state by checking for dropdown menu in DOM
    useEffect(() => {
        const checkDropdownState = () => {
            if (menuDropdownRef.current) {
                const menu = menuDropdownRef.current.querySelector('[role="menu"]');
                setIsDropdownOpen(!!menu);
            }
        };

        const interval = setInterval(checkDropdownState, 100);
        return () => clearInterval(interval);
    }, []);

    return (
        <TooltipContainer
            type="titleOnly"
            title="Video Controls"
            disabled={isDropdownOpen || isMobile || disabled}
            customButton={
                <div ref={menuDropdownRef}>
                    <DropdownMenu
                        items={menuOptions}
                        selectedItem={menuOptions[0]}
                        onSelectItem={(item) => item.action()}
                        getItemLabel={(item) => item.label}
                        getLeftContent={(item) => <i className={`${item.icon} fa-fw`} />}
                        getRightContent={(item) =>
                            item.hotkey && <span className="hotkey">{item.hotkey}</span>
                        }
                        getItemKey={(item) => item.id}
                        buttonClassName="btn2 ghost xsmall square"
                        customMenuButton={<i className="fas fa-ellipsis-v"></i>}
                        menuItemsClassName="left-0 bottom-full mb-1 !w-48"
                        stayOpenAfterSelection={true}
                        disabled={disabled}
                    />
                </div>
            }
        />
    );
};
